import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

interface MobileAppLinks {
  app_store_url: string;
  play_store_url: string;
}

export default function AppDownloadBanner() {
  const [links, setLinks] = useState<MobileAppLinks | null>(null);

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    const { data } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', 'mobile_app_links')
      .maybeSingle();

    if (data?.value) {
      const val = data.value as MobileAppLinks;
      if (val.app_store_url || val.play_store_url) {
        setLinks(val);
      }
    }
  };

  if (!links) return null;

  return (
    <section className="relative py-20 overflow-hidden bg-gradient-to-br from-[#1A472A] via-[#1A472A] to-[#0d2a18]">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white rounded-full translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <DevicePhoneMobileIcon className="w-4 h-4 text-[#FF5722]" />
              <span className="text-white/90 text-sm font-medium">Now Available on Mobile</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
              Take the Tournament<br />With You Everywhere
            </h2>

            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-md">
              Get live scores, real-time leaderboards, tee times, and tournament updates right in your pocket. Download the official Ibom Open app today.
            </p>

            <div className="flex flex-wrap gap-4">
              {links.app_store_url && (
                <a
                  href={links.app_store_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-3 bg-white text-gray-900 rounded-xl px-5 py-3 hover:bg-gray-100 transition-all hover:scale-[1.02] shadow-lg"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-gray-500 leading-none">Download on the</div>
                    <div className="text-base font-semibold leading-tight">App Store</div>
                  </div>
                </a>
              )}

              {links.play_store_url && (
                <a
                  href={links.play_store_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-3 bg-white text-gray-900 rounded-xl px-5 py-3 hover:bg-gray-100 transition-all hover:scale-[1.02] shadow-lg"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-gray-500 leading-none">Get it on</div>
                    <div className="text-base font-semibold leading-tight">Google Play</div>
                  </div>
                </a>
              )}
            </div>
          </motion.div>

          {/* Right: Phone mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative">
              {/* Phone frame */}
              <div className="w-[280px] h-[560px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl ring-1 ring-white/10">
                <div className="w-full h-full bg-gradient-to-b from-[#1A472A] to-[#0d2a18] rounded-[2.4rem] overflow-hidden flex flex-col items-center justify-center relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl" />

                  {/* Screen content mock */}
                  <div className="text-center px-6">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-[#FF5722]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />
                      </svg>
                    </div>
                    <p className="text-white font-bold text-lg mb-1">Ibom Open</p>
                    <p className="text-white/50 text-xs">Live Scores & Updates</p>

                    {/* Mock leaderboard rows */}
                    <div className="mt-6 space-y-2 w-full">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center bg-white/5 rounded-lg px-3 py-2">
                          <span className="text-[#FF5722] font-bold text-xs w-5">{i}</span>
                          <div className="w-5 h-5 bg-white/20 rounded-full ml-1" />
                          <div className="ml-2 flex-1">
                            <div className="h-2 bg-white/20 rounded w-16" />
                          </div>
                          <span className="text-white/80 text-xs font-mono">-{4 - i}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Glow effect */}
              <div className="absolute -inset-4 bg-[#FF5722]/10 rounded-[4rem] blur-2xl -z-10" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
